import { useSignal } from "@preact/signals";
import type { PageArgs } from "bunfx";
import { navigateTo, RedirectError } from "bunfx";
import { AuthLayout } from "../../components/layout";
import {
  Button,
  Form,
  type FormErrors,
  Input,
  Password,
} from "../../components/ui";
import { usePageTitle } from "../../hooks";
import { rpc } from "../../rpc";

type FormState = {
  errors: Record<string, string>;
  loading: boolean;
};

export async function load() {
  // Must be logged in to complete profile
  const { user } = await rpc.auth.me({});
  if (!user) {
    throw new RedirectError("/signup");
  }

  return { email: user.email };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Complete Profile");
  const formState = useSignal<FormState>({ errors: {}, loading: false });

  // FormErrors-compatible lens for Form component
  const errors = {
    get value() {
      return formState.value.errors;
    },
    set value(v: Record<string, string>) {
      formState.value = { ...formState.value, errors: v };
    },
  } as FormErrors;

  async function handleSubmit(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const name = formData.get("name") as string;
    const password = formData.get("password") as string;
    const passwordConfirmation = formData.get("passwordConfirmation") as string;

    // Client-side validation
    const newErrors: Record<string, string> = {};
    if (!name.trim()) {
      newErrors.name = "Name is required";
    }
    if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }
    if (password !== passwordConfirmation) {
      newErrors.passwordConfirmation = "Passwords do not match";
    }

    if (Object.keys(newErrors).length > 0) {
      formState.value = { ...formState.value, errors: newErrors };
      return;
    }

    formState.value = { errors: {}, loading: true };

    try {
      await rpc.auth.setupProfile({ name, password });
      navigateTo("/");
    } catch (err: unknown) {
      if (err && typeof err === "object" && "message" in err) {
        const error = err as { message: string; code?: string; field?: string };
        if (error.field) {
          formState.value = {
            errors: { [error.field]: error.message },
            loading: false,
          };
        } else {
          formState.value = { errors: { form: error.message }, loading: false };
        }
      } else {
        formState.value = {
          errors: { form: "Something went wrong" },
          loading: false,
        };
      }
    }
  }

  return (
    <AuthLayout
      title="Complete your profile"
      subtitle="Just a few more details"
    >
      <Form errors={errors} onSubmit={handleSubmit} class="flex flex-col gap-6">
        {errors.value.form && (
          <div class="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
            {errors.value.form}
          </div>
        )}

        <div class="text-center text-sm text-text-muted mb-2">
          Signing up as <strong class="text-text">{state.value.email}</strong>
        </div>

        <Input
          label="Name"
          name="name"
          type="text"
          autocomplete="name"
          autofocus
          required
        />

        <Password
          label="Password"
          name="password"
          autocomplete="new-password"
          required
        />

        <Password
          label="Confirm password"
          name="passwordConfirmation"
          autocomplete="new-password"
          required
        />

        <Button type="submit" loading={formState.value.loading}>
          Complete signup
        </Button>
      </Form>
    </AuthLayout>
  );
}
